// --- React
import { useCallback, useMemo, useState } from "react";
// --- Config & Utils
import * as columnDefs from "../../../../../../../components/AGGrid/config/columns-def";
import { filterCriteria } from "tap/utils/filterCriteria.js";

/**
 * Custom hook for Job Tasks grid utilities.
 *
 * Features:
 * - Manages sorting state and logic for the Job Tasks grid
 * - Fetches sorted job tasks data from the API
 * - Prepares column definitions with sorting controls
 *
 * @param {object} params - Dependencies and handlers for the grid
 * @returns {object} { setSort, columns }
 */
export function useJobTasksGridUtils({
  tapApi,
  taskFilterForm,
  jobTasksDefaultFilterRange,
  updateJobs,
  handlers,
}) {
  const [sortedColId, setSortedColId] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  // Handle sorting: update state, fetch sorted data, update Job Tasks
  const setSort = useCallback(
    async (colId, direction) => {
      setSortedColId(colId);
      setSortDirection(direction);
      handlers.setLoadingTrue();
      try {
        const data = await tapApi.jobTasks(
          filterCriteria(taskFilterForm, "", "jobTasks"),
          jobTasksDefaultFilterRange,
          [colId, direction.toUpperCase()],
        );
        updateJobs({
          jobTasks: data?.content,
          jobTasksTotalPages: data?.totalPages,
          jobTasksTotalItems: data?.totalElements,
          jobTasksCurrentPage: data?.pageable?.pageNumber,
          jobTasksSortParam: [colId, direction.toUpperCase()],
        });
      } catch (error) {
        console.error("Error sorting job tasks:", error);
      } finally {
        handlers.setLoadingFalse();
      }
    },
    [taskFilterForm, jobTasksDefaultFilterRange, updateJobs, handlers, tapApi],
  );

  // Prepare column definitions with sorting controls
  const baseColumns = columnDefs["tapJobTasks"](...handlers.columnHandlers)[
    "default"
  ];

  const columns = useMemo(
    () =>
      baseColumns.map((col) => ({
        ...col,
        headerComponentParams: {
          ...col.headerComponentParams,
          setSort,
          sortedColId,
          sortDirection,
        },
      })),
    [setSort, sortedColId, sortDirection, baseColumns],
  );

  return { setSort, columns };
}
